import React, { useState, useRef, useEffect, useCallback } from 'react';
import { COLOR_PALETTE } from '../../util/colorCodingUtils';

const ColorPicker = ({ selectedColor, onSelect, onClear, allowClear = true }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, above: false });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !popoverRef.current) return;
    const tRect = triggerRef.current.getBoundingClientRect();
    const pHeight = popoverRef.current.offsetHeight;
    const pWidth = 140;
    const spaceBelow = window.innerHeight - tRect.bottom - 8;
    const above = spaceBelow < pHeight;
    setPos({
      top: above ? tRect.top - pHeight - 4 : tRect.bottom + 4,
      left: Math.max(8, Math.min(tRect.left + tRect.width / 2 - pWidth / 2, window.innerWidth - pWidth - 8)),
      above,
    });
  }, []);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (triggerRef.current?.contains(e.target) || popoverRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Position on open + reposition on scroll/resize
  useEffect(() => {
    if (!open) return;
    // Defer to next frame so popoverRef is measured
    requestAnimationFrame(updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  return (
    <>
      {/* Trigger swatch */}
      <div
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        style={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          border: selectedColor ? `2px solid ${selectedColor}` : '2px dashed #d1d5db',
          background: selectedColor || '#fff',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        title={selectedColor ? 'Change color' : 'Assign color'}
      >
        {!selectedColor && (
          <i className="fa-sharp fa-regular fa-plus" style={{ fontSize: 10, color: '#9ca3af' }} />
        )}
      </div>

      {/* Fixed-position popover — escapes overflow containers */}
      {open && (
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            padding: 8,
            zIndex: 1100,
            width: 140,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
            {COLOR_PALETTE.map((c) => (
              <div
                key={c.accent}
                onClick={() => { onSelect(c.accent); setOpen(false); }}
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  background: c.accent,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: selectedColor === c.accent ? '2px solid #374151' : '2px solid transparent',
                  transition: 'border 0.15s ease',
                }}
                title={c.name}
              >
                {selectedColor === c.accent && (
                  <i className="fa-sharp fa-solid fa-check" style={{ fontSize: 10, color: '#fff' }} />
                )}
              </div>
            ))}
          </div>
          {allowClear && selectedColor && (
            <div
              onClick={() => { onClear(); setOpen(false); }}
              style={{
                marginTop: 6,
                textAlign: 'center',
                fontSize: 11,
                color: '#6b7280',
                cursor: 'pointer',
                padding: '4px 0',
                borderTop: '1px solid #f3f4f6',
              }}
            >
              Clear color
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ColorPicker;
